import { ArrowLeft, Copy, Download, Eye, X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, cx, ErrorNote, Spinner, toast } from "../../components/ui";
import { api, useApi, type Me } from "../../lib/api";
import { active, jobName, size, STATUS_LABEL, STATUS_TONE, TYPE_LABEL, when, type RadarJob } from "./types";

/** One RADAR job (design §15.2, §15.9.5): every id it produced, its push results, and its file. */
export default function RadarJob({ me }: { me: Me }) {
  const { jobId } = useParams();
  const { data: job, error, reload } = useApi<RadarJob>(`/apps/radar/jobs/${jobId}`);
  const [html, setHtml] = useState<string | null>(null);
  const [busy, setBusy] = useState<"view" | "download" | null>(null);
  const reports = me.apps?.find((a) => a.id === "radar")?.roles.includes("reports") ?? false;
  useEffect(() => {
    if (!job || !active(job)) return;
    const t = setInterval(() => void reload(), 4000);
    return () => clearInterval(t);
  }, [job, reload]);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!job) return <Spinner />;
  const isReport = job.type !== "COMPASS_EXPORT";

  const view = async () => {
    setBusy("view");
    try {
      setHtml((await api<{ html: string }>(`/apps/radar/jobs/${job.jobId}/html`)).html);
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(null);
    }
  };
  const download = async () => {
    setBusy("download");
    try {
      window.location.assign((await api<{ url: string }>(`/apps/radar/jobs/${job.jobId}/link`, { body: {} })).url);
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <Link to="/apps/radar" className="inline-flex items-center gap-1 text-sm text-accent-text hover:underline mb-3">
        <ArrowLeft className="size-4" aria-hidden /> RADAR
      </Link>
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-ink">{jobName(job)}</h1>
          <p className="text-sm text-muted">
            {TYPE_LABEL[job.type]} on {job.connId} · started {when(job.createdAt)} by {job.by}
          </p>
        </div>
        <span className={cx("text-sm font-semibold rounded-full px-3 py-1 shrink-0", STATUS_TONE[job.status])}>{STATUS_LABEL[job.status]}</span>
      </div>

      {job.status === "FAILED" && <ErrorNote>{job.error}</ErrorNote>}
      {active(job) && <p className="text-sm text-muted mb-4">{isReport ? "Infor GenAI is writing the report. This usually takes under a minute." : "Fetching the query's result from Compass."} The page updates on its own.</p>}

      {job.status === "COMPLETED" && (
        <div className="flex gap-2 mb-5">
          {isReport && reports && (
            <Button variant="primary" busy={busy === "view"} onClick={() => void view()}>
              <Eye className="size-4" aria-hidden /> View report
            </Button>
          )}
          <Button busy={busy === "download"} onClick={() => void download()}>
            <Download className="size-4" aria-hidden /> Download {isReport ? "HTML" : "CSV"}
          </Button>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Card title="Ids">
          <Row label="RADAR job id">
            <Id value={job.jobId} />
          </Row>
          {job.queryId && (
            <Row label="Compass query id">
              <Id value={job.queryId} />
            </Row>
          )}
          {job.runId && (
            <Row label="Job Automation run">
              <Link to={`/apps/ja/runs/${job.runId}`} className="text-accent-text hover:underline">
                Open the run
              </Link>
              {job.stepId && <span className="text-muted"> · step {job.stepId}</span>}
            </Row>
          )}
          <Row label="Updated">{when(job.updatedAt)}</Row>
        </Card>

        <Card title="Result">
          {job.rows !== undefined && <Row label="Rows">{job.rows}</Row>}
          {job.outputBytes !== undefined && <Row label="Size">{size(job.outputBytes)}</Row>}
          {job.elapsedSeconds !== undefined && <Row label="Time">{job.elapsedSeconds} s</Row>}
          {job.resolved && (
            <Row label="Model">
              {job.resolved.version} ({job.resolved.model}), {job.resolved.maxResponseTokens} tokens max, temperature {job.resolved.temperature}
            </Row>
          )}
          {job.usage && (
            <Row label="Tokens">
              {["input", "output", "cache_read", "cache_write"]
                .filter((k) => job.usage![k] !== undefined)
                .map((k) => `${k.replace("_", " ")} ${job.usage![k]}`)
                .join(" · ")}
              {job.estimatedUsage && <span className="text-muted"> (estimated)</span>}
            </Row>
          )}
          {!job.rows && !job.outputBytes && !job.resolved && <p className="text-sm text-muted">Nothing yet.</p>}
        </Card>

        <Card title="Infor Print Manager">
          {!job.request.pushToInfor ? (
            <p className="text-sm text-muted">Not sent to Print Manager.</p>
          ) : !job.push ? (
            <p className="text-sm text-muted">Sent once the {isReport ? "report" : "export"} is ready.</p>
          ) : (
            <>
              {job.push.printManagerDocumentId && (
                <Row label="Report document">
                  <Id value={job.push.printManagerDocumentId} />
                </Row>
              )}
              {job.push.userReportId && (
                <Row label="User report">
                  <Id value={job.push.userReportId} />
                </Row>
              )}
              {job.request.distProfile && <Row label="Distribution group">{job.request.distProfile}{job.push.distributed ? " (sent)" : ""}</Row>}
              {[job.push.printManagerError, job.push.userReportLookupError, job.push.userReportError].filter(Boolean).map((e) => (
                <p key={e} className="text-sm text-bad">
                  {e}
                </p>
              ))}
            </>
          )}
        </Card>

        {!!job.warnings?.length && (
          <Card title="Warnings">
            <ul className="list-disc pl-5 text-sm text-ink-2 space-y-1">
              {job.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </Card>
        )}
      </div>

      {html !== null && (
        <div className="fixed inset-0 z-50 bg-black/50 flex flex-col p-4" role="dialog" aria-label="Report">
          <div className="flex items-center justify-between bg-surface rounded-t-xl px-4 h-12 border-b border-line">
            <span className="font-medium text-ink">{jobName(job)}</span>
            <Button variant="ghost" aria-label="Close" onClick={() => setHtml(null)}>
              <X className="size-4" />
            </Button>
          </div>
          {/* Model-written HTML: shown with no scripts and no access to the portal (empty sandbox). */}
          <iframe title={jobName(job)} sandbox="" srcDoc={html} className="flex-1 w-full bg-white rounded-b-xl" />
        </div>
      )}
    </div>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-line bg-surface p-4">
      <h2 className="text-sm font-semibold text-ink mb-3">{title}</h2>
      <dl className="space-y-2">{children}</dl>
    </section>
  );
}
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[9rem_1fr] gap-2 text-sm">
      <dt className="text-muted">{label}</dt>
      <dd className="text-ink-2 min-w-0 break-words">{children}</dd>
    </div>
  );
}
function Id({ value }: { value: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <code className="text-xs bg-surface-2 rounded px-1.5 py-0.5 break-all">{value}</code>
      <button
        type="button"
        className="text-muted hover:text-ink"
        aria-label="Copy"
        title="Copy"
        onClick={() => {
          void navigator.clipboard.writeText(value).then(() => toast("Copied"));
        }}
      >
        <Copy className="size-3.5" />
      </button>
    </span>
  );
}
